import * as THREE from 'three';
import { chunkSize, worldHeight } from './TerrainConstants';
import { WorldGenerator } from './WorldGenerator';

const vegetationImpostorDistance = 2048;
const vegetationHysteresis = 0.1;
const treeSpacing = 32;
const grassSpacing = 12;

type TreeType = 'bush' | 'small' | 'large';
interface TreeInstance {
    type: TreeType;
    position: THREE.Vector3;
    scale: number;
    rotation: number;
    color: THREE.Color;
}

interface GrassInstance {
    position: THREE.Vector3;
    scale: number;
    rotation: number;
    color: THREE.Color;
}

interface SharedResources {
    trunkGeometry: THREE.CylinderGeometry;
    foliageGeometry: THREE.ConeGeometry;
    grassGeometry: THREE.BufferGeometry;
    impostorGeometry: THREE.BufferGeometry;
    trunkMaterial: THREE.MeshLambertMaterial;
    foliageMaterial: THREE.MeshLambertMaterial;
    grassMaterial: THREE.MeshBasicMaterial;
    impostorMaterials: Record<TreeType, THREE.MeshBasicMaterial>;
}

let shared: SharedResources | null = null;

function random01(seed: number, x: number, z: number, salt: number): number {
    const value = Math.sin(x * 12.9898 + z * 78.233 + seed * 37.719 + salt * 19.193) * 43758.5453;
    return value - Math.floor(value);
}

function makeImpostorTexture(type: TreeType): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 128;
    const context = canvas.getContext('2d');
    if (context === null) throw new Error('Context 2D not created');
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#5b3825';
    const trunkWidth = type === 'large' ? 12 : type === 'small' ? 7 : 5;
    context.fillRect(32 - trunkWidth / 2, 74, trunkWidth, 54);
    context.fillStyle = type === 'bush' ? '#3c9b38' : '#267b31';
    if (type === 'bush') {
        context.beginPath();
        context.arc(20, 92, 18, 0, Math.PI * 2);
        context.arc(42, 91, 18, 0, Math.PI * 2);
        context.arc(32, 76, 20, 0, Math.PI * 2);
        context.fill();
    }
    else {
        const crownHeight = type === 'large' ? 76 : 60;
        context.beginPath();
        context.moveTo(32, 8);
        context.lineTo(8, 8 + crownHeight);
        context.lineTo(56, 8 + crownHeight);
        context.closePath();
        context.fill();
        if (type === 'large') {
            context.beginPath();
            context.moveTo(32, 35);
            context.lineTo(4, 101);
            context.lineTo(60, 101);
            context.closePath();
            context.fill();
        }
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
}

function createCrossGeometry(width: number, height: number): THREE.BufferGeometry {
    const positions = [
        -width / 2, 0, 0, width / 2, 0, 0, width / 2, height, 0, -width / 2, height, 0,
        0, 0, -width / 2, 0, 0, width / 2, 0, height, width / 2, 0, height, -width / 2,
    ];
    const uvs = [0, 0, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1];
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7]);
    geometry.computeVertexNormals();
    return geometry;
}

function createGrassGeometry(): THREE.BufferGeometry {
    const positions = [
        -0.18, 0, 0, 0.18, 0, 0, 0.03, 1, 0,
        0, 0, -0.18, 0, 0, 0.18, 0, 1, 0.03,
        -0.13, 0, -0.13, 0.13, 0, 0.13, 0.02, 0.95, 0.02,
    ];
    const uvs = [0, 0, 1, 0, 0.5, 1, 0, 0, 1, 0, 0.5, 1, 0, 0, 1, 0, 0.5, 1];
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex([0, 1, 2, 3, 4, 5, 6, 7, 8]);
    geometry.computeVertexNormals();
    return geometry;
}

function getSharedResources(): SharedResources {
    if (shared !== null) return shared;
    const grassGeometry = createGrassGeometry();
    shared = {
        trunkGeometry: new THREE.CylinderGeometry(0.7, 1, 1, 6),
        foliageGeometry: new THREE.ConeGeometry(1, 1, 7),
        grassGeometry,
        impostorGeometry: createCrossGeometry(1, 1),
        trunkMaterial: new THREE.MeshLambertMaterial({ color: 0x7b4b2b }),
        foliageMaterial: new THREE.MeshLambertMaterial({ color: 0x3f9f43, vertexColors: true }),
        grassMaterial: new THREE.MeshBasicMaterial({ color: 0x63b84b, vertexColors: true, side: THREE.DoubleSide }),
        impostorMaterials: {
            bush: new THREE.MeshBasicMaterial({ map: makeImpostorTexture('bush'), transparent: true, alphaTest: 0.35, side: THREE.DoubleSide, depthWrite: true }),
            small: new THREE.MeshBasicMaterial({ map: makeImpostorTexture('small'), transparent: true, alphaTest: 0.35, side: THREE.DoubleSide, depthWrite: true }),
            large: new THREE.MeshBasicMaterial({ map: makeImpostorTexture('large'), transparent: true, alphaTest: 0.35, side: THREE.DoubleSide, depthWrite: true }),
        },
    };
    return shared;
}

export default class VegetationController {
    private readonly scene: THREE.Scene;
    private readonly generator: WorldGenerator;
    private readonly position: THREE.Vector3;
    private readonly group: THREE.Group;
    private readonly geometryGroup: THREE.Group;
    private readonly impostorGroup: THREE.Group;
    private geometryTrees: THREE.InstancedMesh | null = null;
    private geometryFoliage: THREE.InstancedMesh | null = null;
    private grass: THREE.InstancedMesh | null = null;
    private useImpostors = false;

    constructor(scene: THREE.Scene, generator: WorldGenerator, position: THREE.Vector3) {
        this.scene = scene;
        this.generator = generator;
        this.position = position;
        this.group = new THREE.Group();
        this.geometryGroup = new THREE.Group();
        this.impostorGroup = new THREE.Group();
        this.group.position.copy(position);
        this.group.add(this.geometryGroup, this.impostorGroup);
        this.generate();
        this.scene.add(this.group);
    }

    public update(distance: number): void {
        const shouldUseImpostors = this.useImpostors
            ? distance > vegetationImpostorDistance * (1 - vegetationHysteresis)
            : distance > vegetationImpostorDistance * (1 + vegetationHysteresis);
        if (shouldUseImpostors !== this.useImpostors) {
            this.useImpostors = shouldUseImpostors;
            this.geometryGroup.visible = !shouldUseImpostors;
            this.impostorGroup.visible = shouldUseImpostors;
        }
        if (this.grass !== null) this.grass.visible = distance < vegetationImpostorDistance * (1 + vegetationHysteresis);
    }

    public destroy(): void {
        this.scene.remove(this.group);
        this.geometryGroup.clear();
        this.impostorGroup.clear();
        this.geometryTrees = null;
        this.geometryFoliage = null;
        this.grass = null;
    }

    private generate(): void {
        const trees: TreeInstance[] = [];
        const grass: GrassInstance[] = [];
        const max = chunkSize - 1;
        for (let z = treeSpacing / 2; z < max; z += treeSpacing) {
            for (let x = treeSpacing / 2; x < max; x += treeSpacing) {
                const worldX = this.position.x + x;
                const worldZ = this.position.z + z;
                const ground = this.generator.getWorldHeight(worldX, worldZ);
                const density = this.generator.getVegetationDensity(worldX, worldZ);
                const slope = this.generator.getTerrainSlope(worldX, worldZ);
                if (ground <= this.generator.options.waterLevel || slope > 0.65 || random01(this.generator.seed, worldX, worldZ, 1) > density) continue;
                const typeValue = random01(this.generator.seed, worldX, worldZ, 2);
                const type: TreeType = typeValue < 0.25 ? 'bush' : typeValue < 0.78 ? 'small' : 'large';
                trees.push({
                    type,
                    position: new THREE.Vector3(x + (random01(this.generator.seed, worldX, worldZ, 3) - 0.5) * treeSpacing, ground * worldHeight, z + (random01(this.generator.seed, worldX, worldZ, 4) - 0.5) * treeSpacing),
                    scale: (type === 'large' ? 28 : type === 'small' ? 14 : 6) * (0.8 + random01(this.generator.seed, worldX, worldZ, 5) * 0.4),
                    rotation: random01(this.generator.seed, worldX, worldZ, 6) * Math.PI * 2,
                    color: new THREE.Color().setHSL(0.28 + random01(this.generator.seed, worldX, worldZ, 7) * 0.05, 0.55, 0.28 + random01(this.generator.seed, worldX, worldZ, 8) * 0.15),
                });
            }
        }
        for (let z = grassSpacing / 2; z < max; z += grassSpacing) {
            for (let x = grassSpacing / 2; x < max; x += grassSpacing) {
                const worldX = this.position.x + x;
                const worldZ = this.position.z + z;
                const ground = this.generator.getWorldHeight(worldX, worldZ);
                const density = this.generator.getVegetationDensity(worldX, worldZ) * 1.8;
                if (ground <= this.generator.options.waterLevel || this.generator.getTerrainSlope(worldX, worldZ) > 0.7 || random01(this.generator.seed, worldX, worldZ, 9) > Math.min(density, 0.85)) continue;
                grass.push({
                    position: new THREE.Vector3(x + (random01(this.generator.seed, worldX, worldZ, 10) - 0.5) * grassSpacing, ground * worldHeight, z + (random01(this.generator.seed, worldX, worldZ, 11) - 0.5) * grassSpacing),
                    scale: 2 + random01(this.generator.seed, worldX, worldZ, 12) * 3,
                    rotation: random01(this.generator.seed, worldX, worldZ, 13) * Math.PI * 2,
                    color: new THREE.Color().setHSL(0.25 + random01(this.generator.seed, worldX, worldZ, 14) * 0.06, 0.55, 0.32 + random01(this.generator.seed, worldX, worldZ, 15) * 0.12),
                });
            }
        }
        this.createTreeMeshes(trees);
        this.createImpostorMeshes(trees);
        this.createGrassMesh(grass);
    }

    private createTreeMeshes(trees: TreeInstance[]): void {
        if (trees.length === 0) return;
        const resources = getSharedResources();
        this.geometryTrees = new THREE.InstancedMesh(resources.trunkGeometry, resources.trunkMaterial, trees.length);
        const foliageCount = trees.reduce((count, tree) => count + (tree.type === 'large' ? 3 : 1), 0);
        this.geometryFoliage = new THREE.InstancedMesh(resources.foliageGeometry, resources.foliageMaterial, foliageCount);
        const geometryTrees = this.geometryTrees;
        const geometryFoliage = this.geometryFoliage;
        const matrix = new THREE.Matrix4();
        const quaternion = new THREE.Quaternion();
        let foliageIndex = 0;
        trees.forEach((tree, index) => {
            const trunkHeight = tree.type === 'large' ? tree.scale * 0.8 : tree.scale * 0.7;
            matrix.compose(tree.position, quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), tree.rotation), new THREE.Vector3(tree.scale * 0.08, trunkHeight, tree.scale * 0.08));
            geometryTrees.setMatrixAt(index, matrix);
            const foliageLayers = tree.type === 'large' ? 3 : 1;
            for (let layer = 0; layer < foliageLayers; layer++) {
                const y = tree.position.y + tree.scale * (0.55 + layer * 0.22);
                const layerScale = tree.type === 'bush' ? tree.scale * 0.35 : tree.scale * (0.52 - layer * 0.08);
                matrix.compose(new THREE.Vector3(tree.position.x, y, tree.position.z), quaternion, new THREE.Vector3(layerScale, layerScale * 1.25, layerScale));
                geometryFoliage.setMatrixAt(foliageIndex, matrix);
                geometryFoliage.setColorAt(foliageIndex, tree.color);
                foliageIndex++;
            }
        });
        this.geometryTrees.castShadow = false;
        this.geometryFoliage.castShadow = false;
        this.geometryTrees.receiveShadow = false;
        this.geometryFoliage.receiveShadow = false;
        this.geometryGroup.add(this.geometryTrees, this.geometryFoliage);
    }

    private createImpostorMeshes(trees: TreeInstance[]): void {
        const resources = getSharedResources();
        const treeTypes: readonly TreeType[] = ['bush', 'small', 'large'];
        treeTypes.forEach(type => {
            const items = trees.filter(tree => tree.type === type);
            if (items.length === 0) return;
            const mesh = new THREE.InstancedMesh(resources.impostorGeometry, resources.impostorMaterials[type], items.length);
            const matrix = new THREE.Matrix4();
            const quaternion = new THREE.Quaternion();
            items.forEach((tree, index) => {
                const width = type === 'large' ? tree.scale * 1.1 : type === 'small' ? tree.scale * 0.9 : tree.scale * 0.8;
                const height = type === 'large' ? tree.scale * 2.7 : type === 'small' ? tree.scale * 2.2 : tree.scale * 1.3;
                matrix.compose(tree.position, quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), tree.rotation), new THREE.Vector3(width, height, 1));
                mesh.setMatrixAt(index, matrix);
            });
            mesh.castShadow = false;
            mesh.receiveShadow = false;
            this.impostorGroup.add(mesh);
        });
    }

    private createGrassMesh(grass: GrassInstance[]): void {
        if (grass.length === 0) return;
        const resources = getSharedResources();
        this.grass = new THREE.InstancedMesh(resources.grassGeometry, resources.grassMaterial, grass.length);
        const grassMesh = this.grass;
        const matrix = new THREE.Matrix4();
        const quaternion = new THREE.Quaternion();
        grass.forEach((item, index) => {
            matrix.compose(item.position, quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), item.rotation), new THREE.Vector3(item.scale, item.scale, item.scale));
            grassMesh.setMatrixAt(index, matrix);
            grassMesh.setColorAt(index, item.color);
        });
        this.grass.castShadow = false;
        this.grass.receiveShadow = true;
        this.geometryGroup.add(this.grass);
    }
}
